import { useState, useMemo, useCallback } from 'react';
import { TextInput, Toggle } from '@components/ui';
import Accordion from '@components/ui/Accordion/Accordion';
import { useModulesWithPermissions } from '@hooks/useModulesWithPermissions';
import { useTranslation } from '@/hooks/useTranslation';
import type { ModuleWithPermissions } from '@/types/role';
import styles from './PermissionPicker.module.scss';

// ============================================
// TYPES
// ============================================

interface PermissionPickerProps {
  /** Currently selected permission IDs */
  selectedIds: Set<string>;
  /** Callback when selection changes */
  onChange: (ids: Set<string>) => void;
  /** Read-only mode (for ViewRoleModal) */
  readOnly?: boolean;
  /** Whether the picker is active (controls query enabling) */
  enabled?: boolean;
}

// ============================================
// HELPERS
// ============================================

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.3 4L6 11.3L2.7 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

function PermissionPicker({
  selectedIds,
  onChange,
  readOnly = false,
  enabled = true,
}: PermissionPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data: modules, isLoading } = useModulesWithPermissions({ enabled });

  const filteredModules = useMemo(() => {
    if (!modules) return [];
    if (!search.trim()) return modules;
    const q = search.toLowerCase();
    return modules.reduce<ModuleWithPermissions[]>((acc, mod) => {
      const moduleMatches = mod.name.toLowerCase().includes(q);
      const matchingPerms = mod.permissions.filter(
        (p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
      );
      if (moduleMatches) {
        acc.push(mod);
      } else if (matchingPerms.length > 0) {
        acc.push({ ...mod, permissions: matchingPerms });
      }
      return acc;
    }, []);
  }, [modules, search]);

  const handleTogglePermission = useCallback(
    (permissionId: string) => {
      if (readOnly) return;
      const next = new Set(selectedIds);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      onChange(next);
    },
    [selectedIds, onChange, readOnly],
  );

  const handleToggleAll = useCallback(
    (mod: ModuleWithPermissions, isAllSelected: boolean) => {
      if (readOnly) return;
      const next = new Set(selectedIds);
      if (isAllSelected) {
        mod.permissions.forEach((p) => next.delete(p.id));
      } else {
        mod.permissions.forEach((p) => next.add(p.id));
      }
      onChange(next);
    },
    [selectedIds, onChange, readOnly],
  );

  const totalSelected = selectedIds.size;

  if (isLoading) {
    return <div className={styles.loading}>{t('K_LOADING', 'Loading...')}</div>;
  }

  return (
    <div className={styles.picker}>
      {!readOnly && (
        <TextInput
          placeholder={t('K_LABEL_SEARCH_PERMISSIONS', 'Search permissions...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      <div className={styles.summary}>
        {t('K_LABEL_PERMISSIONS_COUNT', '{count} permissions').replace('{count}', String(totalSelected))}
        {' '}{t('K_SELECTED', 'selected')}
      </div>

      <div className={styles.moduleList}>
        {filteredModules.map((mod) => {
          const selectedInModule = mod.permissions.filter((p) => selectedIds.has(p.id)).length;
          const allSelected = selectedInModule === mod.permissions.length && mod.permissions.length > 0;
          const someSelected = selectedInModule > 0 && !allSelected;

          return (
            <Accordion
              key={mod.id}
              title={mod.name}
              subtitle={`${selectedInModule} / ${mod.permissions.length}`}
              variant="filled"
              whiteBody
              defaultExpanded={false}
              action={
                !readOnly ? (
                  <div
                    className={styles.selectAllAction}
                    onClick={(e) => {
                      e.stopPropagation();
                      if ((e.target as HTMLElement).closest('button[role="switch"]')) return;
                      handleToggleAll(mod, allSelected);
                    }}
                  >
                    <Toggle
                      isChecked={allSelected}
                      isMixed={someSelected}
                      onChange={() => handleToggleAll(mod, allSelected)}
                      aria-label={t('K_LABEL_SELECT_ALL', 'Select all')}
                    />
                    <span className={styles.selectAllLabel}>
                      {t('K_LABEL_SELECT_ALL', 'Select all')}
                    </span>
                  </div>
                ) : undefined
              }
            >
              <div className={styles.moduleBody}>
                <div className={styles.permissionList}>
                  {mod.permissions.map((perm) => {
                    const isSelected = selectedIds.has(perm.id);
                    return readOnly ? (
                      <div key={perm.id} className={styles.permissionItem}>
                        <span className={`${styles.readOnlyCheck} ${isSelected ? styles.readOnlyCheckActive : ''}`}>
                          {isSelected && <CheckIcon />}
                        </span>
                        <div className={styles.permissionInfo}>
                          <span className={styles.permissionName}>{perm.name}</span>
                          {perm.description && (
                            <span className={styles.permissionDesc}>{perm.description}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Toggle
                        key={perm.id}
                        variant="card"
                        isChecked={isSelected}
                        onChange={() => handleTogglePermission(perm.id)}
                        label={perm.name}
                        description={perm.description ?? undefined}
                        aria-label={perm.name}
                      />
                    );
                  })}
                </div>
              </div>
            </Accordion>
          );
        })}

        {filteredModules.length === 0 && !isLoading && (
          <p className={styles.emptyText}>
            {search
              ? t('K_EMPTY_NO_MATCHING_PERMISSIONS', 'No permissions match your search.')
              : t('K_EMPTY_NO_PERMISSIONS', 'No permissions available.')}
          </p>
        )}
      </div>
    </div>
  );
}

export default PermissionPicker;
